import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Ban, Frame, Film, Palette, Volume2, VolumeX, Trash2 } from "lucide-react";
import type {
  TimelineClip,
  TimelineAction,
  FilterState,
  ClipFraming,
} from "@scroll/shared-types/timeline";
import { MIN_SPEED, MAX_SPEED, SPEED_PRESETS } from "@scroll/shared-types/timeline";
import { getClipDuration, formatTime } from "@scroll/shared-types/timeline-utils";
import { FILTERS, FILTER_CATEGORIES, type Filter, type FilterCategory } from "../filters-drawer";
import type { TimelineCapabilities } from "@/lib/timeline-capabilities";

type InspectorTab = "speed" | "volume" | "filter" | "framing" | "delete";

const DEFAULT_FRAMING: ClipFraming = {
  fit: "contain",
  positionX: 0.5,
  positionY: 0.5,
  scale: 1,
};

interface ClipInspectorProps {
  clip: TimelineClip;
  clipCount: number;
  onDispatch: (action: TimelineAction) => void;
  onClose: () => void;
  onDelete: () => void;
  capabilities: TimelineCapabilities;
}

export default function ClipInspector({ clip, clipCount, onDispatch, onClose, onDelete, capabilities }: ClipInspectorProps) {
  // Defaults for clips from older localStorage projects
  const speed = clip.speed ?? 1;
  const volume = clip.volume ?? 1;
  const [activeTab, setActiveTab] = useState<InspectorTab>("filter");
  const [activeCategory, setActiveCategory] = useState<FilterCategory>("retro");

  const currentFilter = clip.filter?.filterId
    ? FILTERS.find((f) => f.id === clip.filter!.filterId)
    : null;

  const handleSpeedChange = (speed: number) => {
    onDispatch({ type: "SET_CLIP_SPEED", clipId: clip.id, speed });
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onDispatch({ type: "SET_CLIP_VOLUME", clipId: clip.id, volume: parseFloat(e.target.value) });
  };

  const handleFilterSelect = (filterId: string | null) => {
    const filter: FilterState | null = filterId
      ? { filterId, intensity: clip.filter?.intensity ?? 100 }
      : null;
    onDispatch({ type: "SET_CLIP_FILTER", clipId: clip.id, filter });
  };

  const handleIntensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!clip.filter?.filterId) return;
    onDispatch({
      type: "SET_CLIP_FILTER",
      clipId: clip.id,
      filter: { filterId: clip.filter.filterId, intensity: parseInt(e.target.value) },
    });
  };

  const framing = clip.framing ?? DEFAULT_FRAMING;
  const handleFramingPatch = (patch: Partial<ClipFraming>) => {
    const next: ClipFraming = { ...framing, ...patch };
    // Treat the identity framing as "no framing" so we don't bloat the persisted
    // project with neutral data.
    const isIdentity =
      next.fit === "contain"
      && Math.abs(next.positionX - 0.5) < 1e-6
      && Math.abs(next.positionY - 0.5) < 1e-6
      && (next.scale == null || Math.abs(next.scale - 1) < 1e-6);
    onDispatch({
      type: "SET_CLIP_FRAMING",
      clipId: clip.id,
      framing: isIdentity ? null : next,
    });
  };

  const filteredFilters = FILTERS.filter((f) => f.category === activeCategory);
  const duration = getClipDuration(clip);
  const tabs = useMemo<Array<{
    id: InspectorTab;
    label: string;
    icon: ReactNode;
    badge?: string;
  }>>(
    () => [
      ...(capabilities.supportsSpeed
        ? [{ id: "speed" as const, label: "Speed", icon: <Film size={14} /> }]
        : []),
      ...(capabilities.supportsVolume
        ? [{
            id: "volume" as const,
            label: "Volume",
            icon: volume === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />,
          }]
        : []),
      {
        id: "filter" as const,
        label: "Filter",
        icon: <Palette size={14} />,
        badge: currentFilter?.name,
      },
      ...(capabilities.supportsClipFraming
        ? [{
            id: "framing" as const,
            label: "Framing",
            icon: <Frame size={14} />,
            badge: clip.framing ? "Custom" : undefined,
          }]
        : []),
      ...(clipCount > 1
        ? [{ id: "delete" as const, label: "Delete", icon: <Trash2 size={14} /> }]
        : []),
    ],
    [
      capabilities.supportsSpeed,
      capabilities.supportsVolume,
      capabilities.supportsClipFraming,
      clip.framing,
      clipCount,
      currentFilter?.name,
      volume,
    ],
  );

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id ?? "filter");
    }
  }, [activeTab, tabs]);

  return (
    <div className="clip-inspector">
      <div className="clip-inspector-header">
        <span className="clip-inspector-title">Clip Properties</span>
        <button className="timeline-toolbar-btn" onClick={onClose} title="Close">&times;</button>
      </div>

      {/* Duration info */}
      <div className="clip-inspector-info">
        <span>{formatTime(duration)}</span>
        <span className="clip-inspector-info-dim">
          {speed !== 1 ? `(${speed}x speed)` : ""}
        </span>
      </div>

      <div className="clip-inspector-tabs" role="tablist" aria-label="Clip tools">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`clip-inspector-tab ${activeTab === tab.id ? "active" : ""}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
            {tab.badge && <small>{tab.badge}</small>}
          </button>
        ))}
      </div>

      {activeTab === "speed" && capabilities.supportsSpeed && (
        <div className="clip-inspector-section">
          <div className="clip-inspector-section-header">
            <Film size={14} />
            <span>Speed</span>
          </div>
          <div className="clip-inspector-speed-presets">
            {SPEED_PRESETS.map((s) => (
              <button
                key={s}
                className={`clip-inspector-speed-btn ${speed === s ? "active" : ""}`}
                onClick={() => handleSpeedChange(s)}
              >
                {s}x
              </button>
            ))}
          </div>
          <input
            type="range"
            className="clip-inspector-slider"
            min={MIN_SPEED}
            max={MAX_SPEED}
            step={0.05}
            value={speed}
            onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
          />
          <div className="clip-inspector-slider-labels">
            <span>{MIN_SPEED}x</span>
            <span>{speed.toFixed(2)}x</span>
            <span>{MAX_SPEED}x</span>
          </div>
        </div>
      )}

      {activeTab === "volume" && capabilities.supportsVolume && (
        <div className="clip-inspector-section">
          <div className="clip-inspector-section-header">
            {volume === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />}
            <span>Volume</span>
          </div>
          <input
            type="range"
            className="clip-inspector-slider"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={handleVolumeChange}
          />
          <div className="clip-inspector-slider-labels">
            <span>0%</span>
            <span>{Math.round(volume * 100)}%</span>
            <span>100%</span>
          </div>
        </div>
      )}

      {activeTab === "filter" && (
        <div className="clip-inspector-section">
          <div className="clip-inspector-section-header">
            <Palette size={14} />
            <span>Filter</span>
            {currentFilter && (
              <span className="clip-inspector-filter-badge">{currentFilter.name}</span>
            )}
          </div>

          <div className="clip-inspector-filters">
            {clip.filter?.filterId && (
              <div className="clip-inspector-intensity">
                <span>Intensity</span>
                <input
                  type="range"
                  className="clip-inspector-slider"
                  min={0}
                  max={100}
                  step={1}
                  value={clip.filter.intensity}
                  onChange={handleIntensityChange}
                />
                <span className="clip-inspector-intensity-value">{clip.filter.intensity}</span>
              </div>
            )}

            {/* Category tabs */}
            <div className="clip-inspector-filter-categories">
              <button
                className={`clip-inspector-filter-cat-btn ${!clip.filter?.filterId ? "active" : ""}`}
                onClick={() => handleFilterSelect(null)}
                title="No filter"
              >
                <Ban size={14} />
              </button>
              {FILTER_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  className={`clip-inspector-filter-cat-btn ${activeCategory === cat.id ? "active" : ""}`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            <div className="clip-inspector-filter-grid">
              {filteredFilters.map((filter) => (
                <button
                  key={filter.id}
                  className={`clip-inspector-filter-item ${clip.filter?.filterId === filter.id ? "active" : ""}`}
                  onClick={() => handleFilterSelect(filter.id)}
                >
                  <div
                    className="clip-inspector-filter-swatch"
                    style={{ filter: filter.cssFilter }}
                  />
                  <span>{filter.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "framing" && capabilities.supportsClipFraming && (
        <div className="clip-inspector-section">
          <div className="clip-inspector-section-header">
            <Frame size={14} />
            <span>Fit</span>
          </div>
          <div className="text-inspector-row" role="group" aria-label="Clip framing fit">
            <button
              className={`text-inspector-toggle text-inspector-toggle--wide ${framing.fit === "contain" ? "active" : ""}`}
              onClick={() => handleFramingPatch({ fit: "contain" })}
              aria-pressed={framing.fit === "contain"}
            >
              Fit
            </button>
            <button
              className={`text-inspector-toggle text-inspector-toggle--wide ${framing.fit === "cover" ? "active" : ""}`}
              onClick={() => handleFramingPatch({ fit: "cover" })}
              aria-pressed={framing.fit === "cover"}
            >
              Fill
            </button>
            <button
              className="text-inspector-toggle text-inspector-toggle--wide"
              onClick={() => onDispatch({ type: "SET_CLIP_FRAMING", clipId: clip.id, framing: null })}
              title="Reset framing"
              aria-label="Reset framing to defaults"
            >
              Reset
            </button>
          </div>

          <div className="clip-inspector-section-header clip-inspector-section-header--spaced">
            <Frame size={14} />
            <span>Scale</span>
          </div>
          <input
            type="range"
            className="clip-inspector-slider"
            min={0.5}
            max={2}
            step={0.05}
            value={framing.scale ?? 1}
            onChange={(e) => handleFramingPatch({ scale: parseFloat(e.target.value) })}
            aria-label="Clip scale"
          />
          <div className="clip-inspector-slider-labels">
            <span>0.5×</span>
            <span>{(framing.scale ?? 1).toFixed(2)}×</span>
            <span>2×</span>
          </div>

          <div className="clip-inspector-section-header clip-inspector-section-header--spaced">
            <Frame size={14} />
            <span>Position</span>
          </div>
          <input
            type="range"
            className="clip-inspector-slider"
            min={0}
            max={1}
            step={0.01}
            value={framing.positionX}
            onChange={(e) => handleFramingPatch({ positionX: parseFloat(e.target.value) })}
            aria-label="Horizontal position"
          />
          <div className="clip-inspector-slider-labels">
            <span>Left</span>
            <span>X {Math.round(framing.positionX * 100)}%</span>
            <span>Right</span>
          </div>
          <input
            type="range"
            className="clip-inspector-slider"
            min={0}
            max={1}
            step={0.01}
            value={framing.positionY}
            onChange={(e) => handleFramingPatch({ positionY: parseFloat(e.target.value) })}
            aria-label="Vertical position"
          />
          <div className="clip-inspector-slider-labels">
            <span>Top</span>
            <span>Y {Math.round(framing.positionY * 100)}%</span>
            <span>Bottom</span>
          </div>
        </div>
      )}

      {activeTab === "delete" && clipCount > 1 && (
        <div className="clip-inspector-section">
          <button className="clip-inspector-delete-btn" onClick={onDelete}>
            <Trash2 size={14} />
            <span>Delete Clip</span>
          </button>
        </div>
      )}
    </div>
  );
}
